import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import HeadData from 'components/Common/HeadData'
import FaqStructuredData from 'components/Content/FrequentlyAskedQuestions/FaqStructuredData'
import config from 'constants/config'
import { getImageUrl } from 'lib/image/imageUtils'
import LeLiveHero from 'leLive/components/LeLiveHero'
import LeLiveCountdown from 'leLive/components/LeLiveCountdown'
import LeLivePrizes from 'leLive/components/LeLivePrizes'
import LeLiveHighlights from 'leLive/components/LeLiveHighlights'
import LeLiveVipLounge from 'leLive/components/LeLiveVipLounge'
import LeLivePartners from 'leLive/components/LeLivePartners'
import LeLiveEventDetails from 'leLive/components/LeLiveEventDetails'
import LeLiveFaqs, { getLeLiveFaqs } from 'leLive/components/LeLiveFaqs'
import { registerForLeLive, registerGuestForLeLive, updateLeLiveRegistration, getMyRegistrations } from 'leLive/api/leLive'
import type { LeLiveRegistration } from 'leLive/api/leLive'
import type { RegistrationFormValues } from 'leLive/components/LeLiveRegistrationForm'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { trackClientEvent } from 'analytics/analytics'

const LE_LIVE_CANONICAL_PATH = '/au/live'
const LE_LIVE_IMAGE_ID = 'egpqrc4p9s3oyi84eju6'
const LE_LIVE_START_DATE = '2026-05-16T10:00:00+10:00'
const LE_LIVE_END_DATE = '2026-05-17T18:00:00+10:00'

const REGISTRATION_EVENT_DAY_CATEGORY: Record<RegistrationFormValues['eventDay'], string> = {
  saturday: 'Sat, 16 May',
  sunday: 'Sun, 17 May',
}

function LeLiveLandingPage() {
  const intl = useIntl()
  const loggedIn = useAppSelector(selectLoggedIn)
  const customerPortalURL = useAppSelector((state) => state.system.customerPortalURL)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(loggedIn)
  const [registration, setRegistration] = useState<LeLiveRegistration | undefined>(undefined)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)
  const leLiveEventName = intl.formatMessage(
    {
      id: 'yGMbBsnOM-9-S2rX2AYu',
      defaultMessage: '{brandName} Live 2026',
      description: 'Event name used in structured data markup for the LE Live landing page',
    },
    { brandName: config.brandConfig.name },
  )
  const venueName = intl.formatMessage({
    id: 'F2k7SIYRMLPfa8NpyCho',
    defaultMessage: 'Royal Exhibition Building',
    description: 'Venue name used in the LE Live landing page structured data and SEO copy',
  })
  const venueStreetAddress = intl.formatMessage({
    id: 'wc1ZCxFk3acTTQXl8rSp',
    defaultMessage: '9 Nicholson St',
    description: 'Street address used in LE Live structured data for the event venue postal address',
  })
  const venueLocality = intl.formatMessage({
    id: 'of3w5Dt6Wl33MDVZLBTv',
    defaultMessage: 'Carlton',
    description: 'Locality used in LE Live structured data for the event venue postal address',
  })
  const venueRegion = intl.formatMessage({
    id: 'S0DyM9qJO1cQbjIrRN2_',
    defaultMessage: 'VIC',
    description: 'State or region code used in LE Live structured data for the event venue postal address',
  })
  const venuePostalCode = intl.formatMessage({
    id: '500wOedoqw6kORSiDCrz',
    defaultMessage: '3053',
    description: 'Postal code used in LE Live structured data for the event venue postal address',
  })
  const pageTitle = intl.formatMessage(
    {
      id: 'yRa29kyxzPxPP34ub7xh',
      defaultMessage: '{eventName} — Australia’s Ultimate Travel Event',
      description: 'SEO page title for the LE Live event landing page shown in browser tabs and search results',
    },
    { eventName: leLiveEventName },
  )
  const pageDescription = intl.formatMessage(
    {
      id: '6haQCjydTfhDmxhfB3wz',
      defaultMessage: 'Join 10,000+ travellers at {eventName}, 16–17 May 2026 at the {venueName}, Melbourne. Win thousands in travel prizes. Free entry for members.',
      description: 'Page meta description for LE Live landing page (SEO)',
    },
    { eventName: leLiveEventName, venueName },
  )
  const faqs = useMemo(() => getLeLiveFaqs(intl), [intl])
  const eventStructuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: leLiveEventName,
    description: pageDescription,
    startDate: LE_LIVE_START_DATE,
    endDate: LE_LIVE_END_DATE,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: [getImageUrl(LE_LIVE_IMAGE_ID, { width: 1200, format: 'jpeg' })],
    url: `${customerPortalURL}${LE_LIVE_CANONICAL_PATH}`,
    location: {
      '@type': 'Place',
      name: venueName,
      address: {
        '@type': 'PostalAddress',
        streetAddress: venueStreetAddress,
        addressLocality: venueLocality,
        addressRegion: venueRegion,
        postalCode: venuePostalCode,
        addressCountry: 'AU',
      },
    },
    organizer: {
      '@type': 'Organization',
      name: config.brandConfig.name,
      url: customerPortalURL,
    },
  }), [customerPortalURL, leLiveEventName, pageDescription, venueLocality, venueName, venuePostalCode, venueRegion, venueStreetAddress])

  useEffect(() => {
    if (!loggedIn) {
      setIsLoadingRegistrations(false)
      return
    }

    let cancelled = false
    setIsLoadingRegistrations(true)
    getMyRegistrations().then((result) => {
      if (cancelled) return
      if (result.length > 0) {
        setRegistration(result[0])
      }
    }).catch(() => {
      // silently ignore — user can still register
    }).finally(() => {
      if (!cancelled) setIsLoadingRegistrations(false)
    })

    return () => { cancelled = true }
  }, [loggedIn])

  const handleRegistrationSubmit = useCallback(async(values: RegistrationFormValues) => {
    setIsSubmitting(true)
    setSubmitError(undefined)
    try {
      const hasExistingRegistration = !!registration
      let result: LeLiveRegistration

      if (values.guestEmail && !loggedIn) {
        result = await registerGuestForLeLive({
          email: values.guestEmail,
          ticketQuantity: values.ticketQuantity,
          eventDay: values.eventDay,
          marketingOptIn: values.marketingOptIn,
          phoneNumber: values.phoneNumber,
        })
      } else if (hasExistingRegistration) {
        result = await updateLeLiveRegistration({
          ticketQuantity: values.ticketQuantity,
          eventDay: values.eventDay,
          marketingOptIn: values.marketingOptIn,
          phoneNumber: values.phoneNumber,
        })
      } else {
        result = await registerForLeLive({
          ticketQuantity: values.ticketQuantity,
          eventDay: values.eventDay,
          marketingOptIn: values.marketingOptIn,
          phoneNumber: values.phoneNumber,
        })
      }

      setRegistration(result)

      if (!hasExistingRegistration) {
        trackClientEvent({
          subject: 'le_live_registration',
          category: REGISTRATION_EVENT_DAY_CATEGORY[values.eventDay],
          type: 'interaction',
          action: String(values.ticketQuantity),
        })
      }
    } catch(error: unknown) {
      const statusCode = (error as Record<string, unknown>)?.statusCode
      if (statusCode === 409) {
        if (values.guestEmail && !loggedIn) {
          const message = intl.formatMessage({
            id: '37lAZdknHxgF5NvY1F5W',
            defaultMessage: 'This email is already registered for this day.',
            description: 'Error message when guest tries to register with an email already used for the same LE Live event day',
          })
          setSubmitError(message)
          throw new Error(message)
        }
        if (!registration) {
          try {
            const existing = await getMyRegistrations()
            if (existing.length > 0) {
              setRegistration(existing[0])
              return
            }
          } catch { /* fall through to generic error */ }
        }
      }
      const message = intl.formatMessage({
        id: '8CPHco3jDzt-3zbqxULW',
        defaultMessage: 'Registration failed. Please try again.',
        description: 'Error message shown when LE Live event registration fails',
      })
      setSubmitError(message)
      throw new Error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [registration, loggedIn, intl])

  const hasRegistration = isLoadingRegistrations || !!registration

  return (
    <>
      <HeadData
        title={pageTitle}
        description={pageDescription}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(eventStructuredData)}
        </script>
      </Helmet>
      <FaqStructuredData items={faqs} />

      <main>
        <LeLiveHero
          isSubmitting={isSubmitting}
          isLoadingRegistrations={isLoadingRegistrations}
          registration={registration}
          submitError={submitError}
          onRegistrationSubmit={handleRegistrationSubmit}
        />
        <LeLiveCountdown />
        <LeLivePrizes hasRegistration={hasRegistration} />
        <LeLiveHighlights hasRegistration={hasRegistration} />
        <LeLiveVipLounge />
        <LeLivePartners hasRegistration={hasRegistration} />
        <LeLiveEventDetails hasRegistration={hasRegistration} />
        <LeLiveFaqs />
      </main>
    </>
  )
}

export default LeLiveLandingPage
